/**
 * The signed-in person's look of Paige, kept on the server (`/v1/me/appearance`, docs/engineering/engagement.md,
 * "Appearance"; Dustin, 2026-10-20): the one accent colour the whole product takes, a colour for light and one for
 * dark (null follows Paige blue), and the Show tooltips switch. The app reads it and hands it here
 * (`receivePersonAppearance`), with the way to save it (`setPersonAppearanceSaver`); the workbench's Appearance
 * page reads and changes it through the desktop's own accent and tooltip methods (paige-theme.ts,
 * desktop-adapter.ts), and the dashboard follows every change (`onPersonAppearance`). No browser keeps a copy.
 */
export interface PaigeAppearance {
  accent: { light: string | null; dark: string | null };
  tooltips: boolean;
}

export const DEFAULT_APPEARANCE: PaigeAppearance = {
  accent: { light: null, dark: null },
  tooltips: true,
};

type Saver = (next: PaigeAppearance) => Promise<PaigeAppearance>;
type Listener = (appearance: PaigeAppearance) => void;

let current: PaigeAppearance = DEFAULT_APPEARANCE;
let saver: Saver | null = null;
/** Every change made here counts one; a save answering after a newer change is not shown. */
let revision = 0;
const listeners = new Set<Listener>();

const lower = (color: string | null): string | null => (color ? color.toLowerCase() : null);
const same = (a: PaigeAppearance, b: PaigeAppearance): boolean =>
  a.tooltips === b.tooltips && a.accent.light === b.accent.light && a.accent.dark === b.accent.dark;

function show(next: PaigeAppearance): void {
  const kept = {
    accent: { light: lower(next.accent.light), dark: lower(next.accent.dark) },
    tooltips: next.tooltips,
  };
  if (same(kept, current)) return;
  current = kept;
  listeners.forEach((listener) => listener(kept));
}

/** The appearance in force: the person's, or Paige blue and tooltips until it is read. */
export const personAppearance = (): PaigeAppearance => current;

/** Calls `listener` on every change; returns the stop. */
export function onPersonAppearance(listener: Listener): () => void {
  listeners.add(listener);
  return () => void listeners.delete(listener);
}

/** The person's appearance as the server answered it. */
export function receivePersonAppearance(next: PaigeAppearance): void {
  show(next);
}

/** How a change is kept; null when nobody is signed in (a change then lasts for the page). */
export function setPersonAppearanceSaver(next: Saver | null): void {
  saver = next;
}

/**
 * Shows the change at once and saves it; a refused save puts back what was shown before (unless a newer change
 * came meanwhile) and rejects with the reason, which the Appearance page says.
 */
export async function changePersonAppearance(
  change: (was: PaigeAppearance) => PaigeAppearance,
): Promise<PaigeAppearance> {
  const before = current;
  const mine = ++revision;
  show(change(current));
  if (!saver) return current;
  try {
    const saved = await saver(current);
    if (mine === revision) show(saved);
    return current;
  } catch (error) {
    if (mine === revision) show(before);
    throw error;
  }
}

/** Back to nobody's: Paige blue and tooltips (a sign-out, a test). */
export function forgetPersonAppearance(): void {
  revision++;
  saver = null;
  show(DEFAULT_APPEARANCE);
}
